import styled from "@emotion/styled";
import { Paper, TextField, useMediaQuery, useTheme } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, DatePickerProps } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Dayjs } from "dayjs";
import { forwardRef, useState } from "react";
import React from "react";
import { FieldError } from "react-hook-form";
import { EIconNames } from "src/shared/types/iconNames";
import IconComponent from "../IconComponent";

export const defaultDateFormat = "D.MM.YYYY";

const StyledPaper = styled(Paper)(({ theme }) => ({
  boxShadow: theme.shadows[2],
}));

interface IDatePickerFieldProps extends DatePickerProps<Dayjs> {
  size?: "small" | "medium";
  error?: FieldError;
  placeholder?: string;
  label?: string;
  disableKeyboardInput?: boolean;
}

const DatePickerField = forwardRef<HTMLInputElement, IDatePickerFieldProps>(
  (
    {
      size,
      error,
      label,
      disableKeyboardInput,
      format = defaultDateFormat,
      slots = {
        textField: TextField,
        openPickerIcon: () => (
          <IconComponent iconname={EIconNames.CALENDAR_TODAY} />
        ),
        mobilePaper: StyledPaper,
        desktopPaper: StyledPaper,
      },
      slotProps = {},
      ...props
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
    let inputRef = props.inputRef;

    if (disableKeyboardInput) {
      inputRef = (ref) => {
        if (ref) {
          ref.readOnly = true;
        }
      };
    }

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          {...props}
          format={format}
          ref={ref}
          inputRef={inputRef}
          open={open}
          onClose={() => setOpen(false)}
          slots={slots}
          slotProps={{
            ...slotProps,
            textField: {
              ...slotProps?.textField,
              placeholder: props.placeholder,
              fullWidth: true,
              InputProps: {
                onClick: () => {
                  setOpen((prev) => !prev);
                },
                fullWidth: true,
                ...(isMobile && {
                  endAdornment: (
                    <IconComponent
                      iconname="calendar-today"
                      sx={{ fontSize: "27px !important", opacity: "0.54" }}
                    />
                  ),
                }),
              },
              size: size,
              error: !!error,
              helperText: error?.message,
              label: label,
            },
            field: {
              readOnly: disableKeyboardInput ? true : undefined,
              ...slotProps.field,
            },
            clearButton: {
              sx: {
                padding: 1,
                ["& svg"]: { fontSize: "16px" },
              },
            },
            openPickerButton: {
              sx: {
                marginRight: theme.spacing(-2),
                padding: 1,
                ["& svg"]: { fontSize: "16px" },
              },
            },
          }}
        />
      </LocalizationProvider>
    );
  },
);

DatePickerField.displayName = "DatePickerField";

export default DatePickerField;
